/**
 * Local Database - Fiscal Period Operations
 *
 * CRUD operations for fiscal periods.
 */

import { initializeLocalDb } from './init'
import {
  localStore,
  saveToLocalStorage,
} from './store'

import type { LocalFiscalPeriod } from './types'

/**
 * Get all fiscal periods from local database
 * @param orgId Organization ID
 * @param ledgerId Optional ledger ID to filter by
 */
export async function getLocalFiscalPeriods(orgId: string, ledgerId?: string): Promise<LocalFiscalPeriod[]> {
  // Initialize if needed
  /* v8 ignore next 3 */
  if (!localStore.initialized) {
    await initializeLocalDb(orgId)
  }

  const targetLedgerId = ledgerId ?? localStore.defaultLedgerId

  return Array.from(localStore.fiscalPeriods.values())
    /* v8 ignore next */
    .filter(p => p.orgId === orgId && (!targetLedgerId || p.ledgerId === targetLedgerId))
    .sort((a, b) => {
      // Sort by fiscal year desc, then period number desc
      if (a.fiscalYear !== b.fiscalYear) {return b.fiscalYear - a.fiscalYear}
      return b.periodNumber - a.periodNumber
    })
}

/**
 * Get fiscal period by ID
 */
export function getLocalFiscalPeriod(orgId: string, periodId: string): LocalFiscalPeriod | null {
  const period = localStore.fiscalPeriods.get(periodId)
  return period?.orgId === orgId ? period : null
}

/**
 * Create fiscal year with all monthly periods
 */
export function createLocalFiscalYear(
  orgId: string,
  ledgerId: string,
  fiscalYear: number,
  startMonth: number = 1 // 1 = January
): LocalFiscalPeriod[] {
  const now = new Date().toISOString()
  const periods: LocalFiscalPeriod[] = []

  // Create 12 monthly periods
  for (let month = 0; month < 12; month++) {
    const periodNumber = month + 1
    const actualMonth = ((startMonth - 1 + month) % 12)
    const yearOffset = Math.floor((startMonth - 1 + month) / 12)
    const calendarYear = fiscalYear + yearOffset

    const startDate = new Date(calendarYear, actualMonth, 1)
    const endDate = new Date(calendarYear, actualMonth + 1, 0) // Last day of month

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December']
    const monthName = monthNames[actualMonth]

    const id = crypto.randomUUID()
    const period: LocalFiscalPeriod = {
      id,
      orgId,
      ledgerId,
      name: `${monthName} ${calendarYear}`,
      periodType: 'PERIOD_TYPE_MONTH',
      fiscalYear,
      periodNumber,
      startDate: `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`,
      endDate: `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`,
      status: 'PERIOD_STATUS_OPEN',
      createdAt: now,
      updatedAt: now,
    }

    localStore.fiscalPeriods.set(id, period)
    periods.push(period)
  }

  saveToLocalStorage()
  return periods
}

/**
 * Close a fiscal period
 */
export function closeLocalFiscalPeriod(orgId: string, periodId: string): Promise<LocalFiscalPeriod> {
  const existing = getLocalFiscalPeriod(orgId, periodId)
  if (!existing) {return Promise.reject(new Error('Fiscal period not found'))}
  if (existing.status !== 'PERIOD_STATUS_OPEN') {return Promise.reject(new Error('Period is not open'))}

  const now = new Date().toISOString()
  const updated: LocalFiscalPeriod = {
    ...existing,
    status: 'PERIOD_STATUS_CLOSED',
    closedAt: now,
    closedBy: 'local_user',
    updatedAt: now,
  }

  localStore.fiscalPeriods.set(periodId, updated)
  saveToLocalStorage()
  return Promise.resolve(updated)
}

/**
 * Reopen a closed fiscal period
 */
export function reopenLocalFiscalPeriod(orgId: string, periodId: string): Promise<LocalFiscalPeriod> {
  const existing = getLocalFiscalPeriod(orgId, periodId)
  if (!existing) {return Promise.reject(new Error('Fiscal period not found'))}
  if (existing.status !== 'PERIOD_STATUS_CLOSED') {return Promise.reject(new Error('Period is not closed'))}

  const now = new Date().toISOString()
  const { closedAt: _, closedBy: _closedBy, ...rest } = existing
  const updated: LocalFiscalPeriod = {
    ...rest,
    status: 'PERIOD_STATUS_OPEN',
    updatedAt: now,
  }

  localStore.fiscalPeriods.set(periodId, updated)
  saveToLocalStorage()
  return Promise.resolve(updated)
}

/**
 * Lock a fiscal period (permanent - cannot be unlocked)
 */
export function lockLocalFiscalPeriod(orgId: string, periodId: string): Promise<LocalFiscalPeriod> {
  const existing = getLocalFiscalPeriod(orgId, periodId)
  if (!existing) {return Promise.reject(new Error('Fiscal period not found'))}
  if (existing.status === 'PERIOD_STATUS_LOCKED') {return Promise.reject(new Error('Period is already locked'))}

  const now = new Date().toISOString()
  const updated: LocalFiscalPeriod = {
    ...existing,
    status: 'PERIOD_STATUS_LOCKED',
    lockedAt: now,
    lockedBy: 'local_user',
    closedAt: existing.closedAt ?? now,
    closedBy: existing.closedBy ?? 'local_user',
    updatedAt: now,
  }

  localStore.fiscalPeriods.set(periodId, updated)
  saveToLocalStorage()
  return Promise.resolve(updated)
}

/**
 * Get fiscal years that have been created
 */
export async function getLocalFiscalYears(orgId: string, ledgerId?: string): Promise<number[]> {
  const periods = await getLocalFiscalPeriods(orgId, ledgerId)
  const years = new Set(periods.map(p => p.fiscalYear))
  return Array.from(years).sort((a, b) => b - a)
}
